import { IChecklistCategory, IChecklistItem, IChecklistItemRow, IChecklistPopulated } from "@mctypes/checklist.types";
import { IIssueApi } from "@schemas/issueSchema";

const normalizeId = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return value.toString();
  if (typeof value === "object") {
    const idValue = value as {
      _id?: unknown;
      id?: unknown;
      $oid?: unknown;
      toHexString?: () => string;
      toString?: () => string;
    };

    if (typeof idValue.toHexString === "function") return idValue.toHexString();
    if (idValue.$oid != null) return normalizeId(idValue.$oid);
    if (idValue._id != null) return normalizeId(idValue._id);
    if (idValue.id != null) return normalizeId(idValue.id);
    if (typeof idValue.toString === "function" && idValue.toString !== Object.prototype.toString) return idValue.toString();
  }

  return "";
};

const sameId = (left: unknown, right: unknown) => normalizeId(left) === normalizeId(right);

export const getChecklistTableRowsRefactored = (
  checklist: IChecklistPopulated,
  checklistCategories: IChecklistCategory[],
  checklistItems: IChecklistItem[],
): IChecklistItemRow[] => {
  if (checklist == null || checklistCategories == null) {
    return [];
  }

  return checklistCategories.flatMap((cat) => {
    const categoryItems = checklistItems.filter((item) => sameId(item.category, cat._id));
    const categoryHasItems = categoryItems.length > 0;
    let categoryInactive = categoryHasItems;

    let itemRows: IChecklistItemRow[] = categoryItems.map((item) => {
        if (item.inactive?.isChecked === false) {
          categoryInactive = false;
        }

        return {
          _id: item._id,
          originalCategory: cat,
          inactive: item.inactive,
          title: item.title,
          reference: item.reference ?? "",
          collapsed: cat.collapsed === true,
          originalItem: item,
          comment: item.comment,
          checks: {
            check1: item.checks?.check1,
            check2: item.checks?.check2,
            check3: item.checks?.check3,
            check4: item.checks?.check4,
            check5: item.checks?.check5,
            check6: item.checks?.check6,
            check7: item.checks?.check7,
            check8: item.checks?.check8,
            check9: item.checks?.check9,
            check10: item.checks?.check10,
            check11: item.checks?.check11,
            check12: item.checks?.check12,
            check13: item.checks?.check13,
            check14: item.checks?.check14,
            check15: item.checks?.check15,
          },
          itemId: item.itemId,
          categoryId: cat.categoryId,
          rowId: `${cat.categoryId}.${item.itemId}`,
          connectedIssues: item.connectedIssues as IIssueApi[],
          comments: item.comments,
          itemTags: item.itemTags ?? [],
        };
      });

    if (
      checklist.itemTagsActive === true &&
      (checklist.activeItemTags?.length ?? 0) > 0 &&
      itemRows.length > 0
    ) {
      itemRows = itemRows.filter((row) => checklist.activeItemTags.every((tag) => row.itemTags.includes(tag)));
    }

    if (
      checklist.itemTagsActive === true &&
      (checklist.activeItemTags?.length ?? 0) > 0 &&
      categoryHasItems === true &&
      itemRows.length === 0
    ) {
      return [];
    }

    const categoryRow: IChecklistItemRow = {
      _id: cat._id,
      originalCategory: cat,
      dueDate: cat.dueDate,
      inactive: {
        isChecked: categoryInactive === true && categoryHasItems === true,
        updatedDate: null,
        updatedBy: null,
      },
      title: cat.title,
      reference: "",
      collapsed: cat.collapsed === true,
      originalItem: null,
      comment: null,
      checks: null,
      itemId: null,
      categoryId: cat.categoryId,
      rowId: cat.categoryId.toString(),
    };

    return [categoryRow, ...itemRows];
  });
};

export const getActiveChecklistCheckKeys = (checklist: IChecklistPopulated): string[] => {
  return Object.entries(checklist?.checkTypes ?? {})
    .filter(([, checkType]: [string, any]) => checkType?.active === true)
    .map(([key]) => key);
};
